import { useState, useEffect, ReactNode } from 'react';
import { Box, Typography, Collapse, SxProps, Theme } from '@mui/material';
import { Card } from './Card';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  getAccordionHeaderSx,
  accordionTitleSx,
  accordionChevronSx,
  accordionContainerSx
} from './Accordion.styles';

export interface AccordionProps {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
  headerSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  chevronSx?: SxProps<Theme>;
  containerSx?: React.CSSProperties;
  contentSx?: SxProps<Theme>;
  onToggle?: (isExpanded: boolean) => void;
}

export const Accordion = ({
  title,
  children,
  defaultExpanded = true,
  headerSx,
  titleSx,
  chevronSx,
  containerSx,
  contentSx,
  onToggle
}: AccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showHeaderMargin, setShowHeaderMargin] = useState(defaultExpanded);

  useEffect(() => {
    if (isExpanded) {
      // Immediately show margin when opening
      setShowHeaderMargin(true);
    } else {
      // Delay removing margin when closing to match collapse animation
      const timer = setTimeout(() => {
        setShowHeaderMargin(false);
      }, 300); // Match the collapse animation duration
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle?.(newExpanded);
  };

  const defaultHeaderSx = getAccordionHeaderSx(showHeaderMargin);
  const mergedContainerSx: React.CSSProperties = containerSx
    ? { ...accordionContainerSx, ...containerSx }
    : accordionContainerSx;

  return (
    <Card sx={mergedContainerSx}>
      <Box
        sx={
          headerSx
            ? ([defaultHeaderSx, headerSx] as SxProps<Theme>)
            : defaultHeaderSx
        }
        onClick={handleToggle}
      >
        <Typography
          sx={
            titleSx
              ? ([accordionTitleSx, titleSx] as SxProps<Theme>)
              : accordionTitleSx
          }
        >
          {title}
        </Typography>
        {isExpanded ? (
          <ExpandLess
            sx={
              chevronSx
                ? ([accordionChevronSx, chevronSx] as SxProps<Theme>)
                : accordionChevronSx
            }
          />
        ) : (
          <ExpandMore
            sx={
              chevronSx
                ? ([accordionChevronSx, chevronSx] as SxProps<Theme>)
                : accordionChevronSx
            }
          />
        )}
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={contentSx} key={`accordion-content-${title}`}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
};

